import { Component, output, ChangeDetectionStrategy } from '@angular/core';
import { RouterLink } from '@angular/router';
import { GUIDES } from '@core/data/guides.data';
import { SEARCH_LOCATIONS } from '@core/data/search-locations.data';
import { LucideDynamicIcon } from '@lucide/angular';
import {
  FacebookIcon as Facebook,
  InstagramIcon as Instagram,
  LinkedinIcon as Linkedin,
  TwitterIcon as Twitter,
} from '../custom-lucide-icon/brand.icons';

interface FooterLink {
  label: string;
  href: string;
  action?: string;
  routerLink?: boolean;
}

interface FooterSection {
  title: string;
  links: FooterLink[];
}

// Inbound-link block for Tier 3 SEO pages. The audit showed nearly every
// region/guide page has only sitemap.xml as a referring URL, which Google
// treats as a low-priority signal ("Discovered - currently not indexed").
// Surfacing them in the footer gives each page at least one site-wide
// internal link so the crawl-budget signal flips positive.
const REGION_LINKS: FooterLink[] = SEARCH_LOCATIONS.map((loc) => ({
  label: `Motorhome hire ${loc.name}`,
  href: `/search/${loc.slug}`,
  routerLink: true,
}));

const GUIDE_LINKS: FooterLink[] = GUIDES.map((g) => ({
  label: g.title,
  href: `/guides/${g.slug}`,
  routerLink: true,
}));

@Component({
  selector: 'app-footer',
  imports: [LucideDynamicIcon, RouterLink],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './footer.html',
})
export class Footer {
  navigate = output<string>();

  currentYear = new Date().getFullYear();

  // Lucide icons
  readonly FacebookIcon = Facebook;
  readonly TwitterIcon = Twitter;
  readonly InstagramIcon = Instagram;
  readonly LinkedinIcon = Linkedin;

  readonly regionLinks = REGION_LINKS;
  readonly guideLinks = GUIDE_LINKS;

  footerSections: FooterSection[] = [
    {
      title: 'Company',
      links: [
        { label: 'About us', href: '/about', routerLink: true },
        // { label: 'Careers', href: '/careers', routerLink: true },
        { label: 'Press', href: '/press', routerLink: true },
        { label: 'Blog', href: '/blog', routerLink: true },
      ],
    },
    {
      title: 'Support',
      links: [
        { label: 'Help center', href: '/help', routerLink: true },
        { label: 'Safety information', href: '/safety', routerLink: true },
        { label: 'Cancellation Policy', href: '/cancellation', routerLink: true },
        { label: 'Contact us', href: '/contact', routerLink: true },
      ],
    },
    {
      title: 'Hosting',
      links: [
        { label: 'List your motorhome', href: '/list-your-motorhome', routerLink: true },
        { label: 'Speedy Import', href: '/speedy-import', routerLink: true },
        { label: 'Join an organisation', href: '/join', routerLink: true },
        { label: 'Managing your team', href: '/managing-your-team', routerLink: true },
        { label: 'Host resources', href: '/host-resources', routerLink: true },
        { label: 'Host cancellation policy', href: '/host-cancellation-policy', routerLink: true },
        // { label: 'Community forum', href: '/community', routerLink: true },
      ],
    },
    {
      title: 'Legal',
      links: [
        { label: 'Terms of service', href: '/terms', routerLink: true },
        { label: 'Privacy policy', href: '/privacy', routerLink: true },
        { label: 'Cookie policy', href: '/cookies', routerLink: true },
        { label: 'Sitemap', href: '/sitemap', routerLink: true },
      ],
    },
  ];

  socialLinks = [
    {
      icon: 'facebook',
      label: 'Facebook',
      href: 'https://www.facebook.com/share/1Did4yQeV6/',
      // href: 'https://www.facebook.com/profile.php?id=61585699155759',
    },
    // { icon: 'twitter', label: 'Twitter', href: '#' },
    // { icon: 'instagram', label: 'Instagram', href: '#' },
    // { icon: 'linkedin', label: 'LinkedIn', href: '#' },
  ];

  onLinkClick(action?: string) {
    if (action) {
      this.navigate.emit(action);
    }
  }
}
